import React, { useEffect, useRef, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { KeyboardAvoidingView, Platform, ScrollView, StyleSheet, Text, TextInput, View } from '../components/rn';
import PageHeader from '../components/PageHeader';
import { screenHeaderContentInset } from '../components/screenHeaderLayout';
import PrimaryActionButton from '../components/PrimaryActionButton';
import GoogleAccountButton, { googleAccountButtonAvailable } from '../components/GoogleAccountButton';
import { useTranslation } from '../i18n/I18nContext';
import { useTheme } from '../theme/ThemeContext';
import { profileFieldStyle } from '../theme/profileSection';
import { spacing, fontSize } from '../theme';
import { googleAuth, googleSignInEnabled } from '../auth/googleAuth';
import { createGoogleLinkController, initialGoogleLinkState } from '../auth/googleLinkController';
import { api } from '../services/api';
import { normalizeInputDigits } from '../i18n/inputDigits';

// Parent keys this screen by session so credentials cannot cross accounts.
export default function GoogleLinkScreen({ token, onBack }: { token: string; onBack: () => void }) {
  const { t, lang } = useTranslation();
  const colors = useTheme();
  const insets = useSafeAreaInsets();
  const [state, setState] = useState(initialGoogleLinkState);
  const [code, setCode] = useState('');
  const flow = useRef<ReturnType<typeof createGoogleLinkController> | null>(null);
  const enabled = googleSignInEnabled() && googleAccountButtonAvailable();
  useEffect(() => {
    if (!enabled) return;
    const controller = createGoogleLinkController(token, { ...api, link: (proof, signal) => googleAuth.link(proof, signal) }, setState);
    flow.current = controller;
    setState(initialGoogleLinkState);
    setCode('');
    controller.refresh();
    return () => { controller.dispose(); flow.current = null; };
  }, [token, enabled]);
  const back = () => { flow.current?.dispose(); setCode(''); onBack(); };
  const phaseText = !enabled ? t('signInMethods.googleUnavailable')
    : state.phase === 'linked' ? t('signInMethods.googleLinked')
    : state.phase === 'checking' ? t('common.loading')
    : state.phase === 'code' ? t('signInMethods.googleCodeSent', { phone: state.phoneHint })
    : state.phase === 'choose' ? t('signInMethods.googleChooseHint')
    : state.phase === 'unknown' ? t('signInMethods.googleStatusUnknown')
    : t('signInMethods.googleLinkIntro');
  return <View style={[styles.screen, { backgroundColor: colors.white }]}>
    <PageHeader title="Google" onBack={back} floating />
    <KeyboardAvoidingView style={styles.screen} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={[styles.content, { paddingTop: screenHeaderContentInset(insets.top), paddingBottom: insets.bottom + spacing.xl }]}>
        <Text testID="google-link-status" accessibilityLiveRegion="polite" style={[styles.text, { color: colors.black }]}>{phaseText}</Text>
        {state.error && state.phase !== 'unknown' && <Text testID="google-link-error" accessibilityLiveRegion="polite"
          style={[styles.text, { color: colors.gray600 }]}>{t('signInMethods.googleLinkFailed')}</Text>}
        {enabled && state.phase === 'ready' && <PrimaryActionButton testID="google-link-send"
          label={t('signInMethods.googleSendCode')} loading={state.busy} onPress={() => { setCode(''); flow.current?.send(); }} />}
        {enabled && state.phase === 'unknown' && <PrimaryActionButton testID="google-link-refresh"
          label={t('common.retry')} loading={state.busy} onPress={() => { flow.current?.refresh(); }} />}
        {enabled && state.phase === 'code' && <>
          <TextInput testID="google-link-code" accessibilityLabel={t('signInMethods.googleCodeLabel')}
            style={[profileFieldStyle(colors), styles.code, { color: colors.black }]}
            value={code} onChangeText={value => setCode(normalizeInputDigits(value, lang).replace(/[^0-9]/g, '').slice(0, 10))}
            editable={!state.busy} keyboardType="number-pad" textContentType="oneTimeCode" autoComplete="sms-otp"
            maxLength={10} autoFocus />
          <PrimaryActionButton testID="google-link-verify" label={t('otp.verify')}
            disabled={!/^\d{4,10}$/.test(code)} loading={state.busy}
            onPress={() => { const entered = code; setCode(''); flow.current?.verify(entered); }} />
          <PrimaryActionButton testID="google-link-restart" label={t('signInMethods.googleStartAgain')}
            disabled={state.busy} onPress={() => { setCode(''); flow.current?.restart(); }} />
        </>}
        {enabled && state.phase === 'choose' && <GoogleAccountButton label={t('signInMethods.googleContinue')}
          disabled={state.busy} onPress={() => { flow.current?.choose(); }} />}
      </ScrollView>
    </KeyboardAvoidingView>
  </View>;
}
const styles = StyleSheet.create({
  screen: { flex: 1 }, content: { padding: spacing.lg, gap: spacing.md },
  text: { fontSize: fontSize.md, lineHeight: 24, flexShrink: 1 }, code: { fontSize: fontSize.md },
});
